import React from 'react';
import { 
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import 'react-accessible-accordion/dist/fancy-example.css';

// TypeScript interfaces
interface JobType {
  group: string;
  title: string;
}

interface Skill {
  id: number;
  name: string;
  color: string;
  category: string;
}

interface FormElements {
  favorites: boolean;
  job_types_group_eq: string;
  hourly_rate_gteq: string;
  hourly_rate_lteq: string;
  position_types_id_in: string[];
  skills_id_in: string[];
}

interface ProfileFiltersProps {
  form_elements: FormElements;
  job_types: JobType[];
  location: string;
  miles: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onChangeSort?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onToggleFavorites: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeLocation: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onChangeCheckbox: (event: React.ChangeEvent<HTMLInputElement>) => void;
  skill_categories: string[];
  skills: Skill[];
}

interface TagProps {
  form_elements: FormElements;
  skill: Skill;
  onChangeCheckbox: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// Design tokens - matching JobFilters
const tokens = {
  colors: {
    primary: '#085ec8',
    primaryLight: '#0a6fd9',
    text: '#1a365d',
    textMuted: '#64748b',
    textBody: '#495057',
    border: '#e2e8f0',
    borderFocus: '#085ec8',
    bg: '#ffffff',
    bgHover: '#f8fafc',
    bgSubtle: '#f1f5f9',
    success: '#059669',
    heart: '#dc3545',
  },
  radius: {
    sm: '6px',
    md: '10px',
    lg: '14px',
  },
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem 1rem',
  borderRadius: tokens.radius.md,
  border: `1px solid ${tokens.colors.border}`,
  fontSize: '0.9375rem',
  color: tokens.colors.text,
  backgroundColor: tokens.colors.bg,
  transition: 'all 0.2s ease',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: tokens.colors.text,
  marginBottom: '0.5rem',
  letterSpacing: '0.01em',
};

/**
 * Tag Component - Individual skill tag with checkbox
 * Modern Clinical design with enhanced visual feedback
 */
const Tag: React.FC<TagProps> = ({ form_elements, skill, onChangeCheckbox }) => {
  const skillId = `check-skill-${skill.id}`;
  const isChecked = form_elements.skills_id_in.includes(`${skill.id}`);

  return (
    <label
      htmlFor={skillId}
      id={`${skillId}-label`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.375rem',
        padding: '0.375rem 0.75rem',
        borderRadius: '100px',
        fontSize: '0.8125rem',
        fontWeight: isChecked ? 600 : 500,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        background: isChecked ? skill.color : `${skill.color}15`,
        color: isChecked ? 'white' : skill.color,
        border: `1.5px solid ${isChecked ? skill.color : `${skill.color}40`}`,
        margin: '0.25rem',
        boxShadow: isChecked ? `0 2px 8px ${skill.color}40` : 'none',
      }}
    >
      <input
        id={skillId}
        type="checkbox"
        name="skills_id_in"
        value={`${skill.id}`}
        onChange={onChangeCheckbox}
        checked={isChecked}
        aria-describedby={`${skillId}-label`}
        style={{ display: 'none' }}
      />
      <span style={{
        width: '14px',
        height: '14px',
        borderRadius: '4px',
        border: `1.5px solid ${isChecked ? 'rgba(255,255,255,0.6)' : skill.color}`,
        background: isChecked ? 'rgba(255,255,255,0.25)' : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.2s ease',
      }}>
        {isChecked && (
          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        )}
      </span>
      {skill.name}
    </label>
  );
};

/**
 * ProfileFilters Component - Enhanced TypeScript version
 * Provides advanced filtering options for profile search
 * Includes location, skills, position types, and pay rate filters
 */
export const ProfileFilters: React.FC<ProfileFiltersProps> = ({
  form_elements,
  job_types,
  location,
  miles,
  onChange,
  onChangeSort,
  onToggleFavorites,
  onChangeLocation,
  onChangeCheckbox,
  skill_categories,
  skills,
}) => {
  // Position types configuration
  const positionTypes = [
    { id: '1', label: 'Full Time', inputId: 'check-fulltime', color: '#3498db' },
    { id: '2', label: 'Part Time', inputId: 'check-parttime', color: '#9b59b6' },
    { id: '3', label: 'Long Term Temp', inputId: 'check-longterm', color: '#f39c12' },
    { id: '4', label: 'Temporary', inputId: 'check-temp', color: '#e74c3c' },
  ];

  // Miles options for location search
  const milesOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 75, 100];

  return (
    <div style={{
      background: tokens.colors.bg,
      border: `1px solid ${tokens.colors.border}`,
      borderRadius: tokens.radius.lg,
      boxShadow: tokens.shadow.md,
    }}>
      {/* Header */}
      <div style={{
        padding: '1.25rem 1.5rem',
        borderBottom: `1px solid ${tokens.colors.border}`,
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} 0 0`,
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          fontWeight: 700,
          color: tokens.colors.text,
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={tokens.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          Refine Search
        </h3>
      </div>

      {/* Body */}
      <div style={{ padding: '1.5rem' }}>
        {/* Favorites Toggle */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label
            htmlFor="check-favorites"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.875rem 1rem',
              borderRadius: tokens.radius.md,
              border: `1px solid ${form_elements.favorites ? tokens.colors.heart : tokens.colors.border}`,
              background: form_elements.favorites ? `${tokens.colors.heart}08` : tokens.colors.bg,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <input
              id="check-favorites"
              type="checkbox"
              name="favorites"
              value="1"
              onChange={onToggleFavorites}
              checked={form_elements.favorites}
              style={{ display: 'none' }}
            />
            <span style={{
              width: '18px',
              height: '18px',
              borderRadius: '4px',
              border: `2px solid ${form_elements.favorites ? tokens.colors.heart : tokens.colors.border}`,
              background: form_elements.favorites ? tokens.colors.heart : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'all 0.2s ease',
            }}>
              {form_elements.favorites && (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </span>
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: form_elements.favorites ? 600 : 500,
              color: form_elements.favorites ? tokens.colors.heart : tokens.colors.textMuted,
              fontSize: '0.875rem',
            }}>
              <i className="fa fa-heart" style={{ color: tokens.colors.heart }}></i>
              Show Favorites Only
            </span>
          </label>
        </div>

        {/* Position/Job Type */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="search_job_type_title_cont" style={labelStyle}>Position</label>
          <select
            name="job_types_group_eq"
            id="search_job_type_title_cont"
            onChange={onChange}
            value={form_elements.job_types_group_eq}
            aria-label="Select position type"
            style={{
              ...inputStyle,
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              paddingRight: '2.5rem',
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = tokens.colors.borderFocus}
            onBlur={(e) => e.currentTarget.style.borderColor = tokens.colors.border}
          >
            <option value="">All Positions</option>
            {job_types.map((jt, index) => (
              <option key={`${jt.group}-${jt.title}-${index}`} value={`${jt.group}-${jt.title}`}>
                {jt.title}
              </option>
            ))}
          </select>
        </div>

        {/* Location Group */}
        <div style={{
          background: tokens.colors.bgSubtle,
          borderRadius: tokens.radius.md,
          padding: '1rem',
          marginBottom: '1.5rem',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.875rem',
            color: tokens.colors.textMuted,
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            Location
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <input
                type="text"
                name="location"
                id="location"
                value={location}
                onChange={onChangeLocation}
                placeholder="Zip code"
                style={{
                  ...inputStyle,
                  backgroundColor: tokens.colors.bg,
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = tokens.colors.borderFocus}
                onBlur={(e) => e.currentTarget.style.borderColor = tokens.colors.border}
              />
            </div>
            <div style={{ width: '110px' }}>
              <select
                name="miles"
                id="miles"
                value={miles}
                onChange={onChangeLocation}
                style={{
                  ...inputStyle,
                  backgroundColor: tokens.colors.bg,
                  cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  paddingRight: '1.75rem',
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = tokens.colors.borderFocus}
                onBlur={(e) => e.currentTarget.style.borderColor = tokens.colors.border}
              >
                {milesOptions.map(mile => (
                  <option key={mile} value={mile.toString()}>
                    {mile} mi
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Pay Rate Group */}
        <div style={{
          background: tokens.colors.bgSubtle,
          borderRadius: tokens.radius.md,
          padding: '1rem',
          marginBottom: '1.5rem',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.875rem',
            color: tokens.colors.textMuted,
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
            Pay Rate ($/hr)
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Min"
              name="hourly_rate_gteq"
              onChange={onChange}
              value={form_elements.hourly_rate_gteq}
              aria-label="Minimum hourly rate"
              style={{
                ...inputStyle,
                backgroundColor: tokens.colors.bg,
                flex: 1,
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = tokens.colors.borderFocus}
              onBlur={(e) => e.currentTarget.style.borderColor = tokens.colors.border}
            />
            <span style={{ color: tokens.colors.textMuted, fontSize: '0.75rem', flexShrink: 0 }}>to</span>
            <input
              type="text"
              placeholder="Max"
              name="hourly_rate_lteq"
              onChange={onChange}
              value={form_elements.hourly_rate_lteq}
              aria-label="Maximum hourly rate"
              style={{
                ...inputStyle,
                backgroundColor: tokens.colors.bg,
                flex: 1,
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = tokens.colors.borderFocus}
              onBlur={(e) => e.currentTarget.style.borderColor = tokens.colors.border}
            />
          </div>
        </div>

        {/* Position Type */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={labelStyle}>Position Type</label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '0.5rem',
          }}>
            {positionTypes.map(({ id, label, inputId, color: typeColor }) => {
              const isChecked = form_elements.position_types_id_in.includes(id);
              return (
                <label
                  key={id}
                  htmlFor={inputId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    padding: '0.625rem 0.75rem',
                    borderRadius: tokens.radius.sm,
                    border: `1px solid ${isChecked ? typeColor : tokens.colors.border}`,
                    background: isChecked ? `${typeColor}08` : tokens.colors.bg,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '0.8125rem',
                    fontWeight: isChecked ? 600 : 500,
                    color: isChecked ? typeColor : tokens.colors.textMuted,
                  }}
                >
                  <input
                    id={inputId}
                    type="checkbox"
                    name="position_types_id_in"
                    value={id}
                    onChange={onChangeCheckbox}
                    checked={isChecked}
                    style={{ display: 'none' }}
                  />
                  <span style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '4px',
                    border: `2px solid ${isChecked ? typeColor : tokens.colors.border}`,
                    background: isChecked ? typeColor : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.2s ease',
                  }}>
                    {isChecked && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </span>
                  {label}
                </label>
              );
            })}
          </div>
        </div>

        {/* Skills Filter */}
        <div style={{ marginBottom: '0' }}>
          <label style={labelStyle}>Skills</label>

          {/* Custom Accordion Styles */}
          <style>{`
            .skills-accordion .accordion__item {
              border: 1px solid #e2e8f0;
              border-radius: 10px;
              margin-bottom: 0.5rem;
              overflow: hidden;
              background: white;
              transition: all 0.2s ease;
            }
            .skills-accordion .accordion__item:hover {
              border-color: #cbd5e1;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            }
            .skills-accordion .accordion__button {
              background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
              padding: 0.875rem 1rem;
              font-size: 0.875rem;
              font-weight: 600;
              color: #1a365d;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: space-between;
              border: none;
              width: 100%;
              text-align: left;
              transition: all 0.2s ease;
            }
            .skills-accordion .accordion__button:hover {
              background: linear-gradient(135deg, #f1f5f9 0%, #f8fafc 100%);
            }
            .skills-accordion .accordion__button::before {
              display: none !important;
            }
            .skills-accordion .accordion__button::after {
              content: '';
              display: inline-block;
              width: 8px;
              height: 8px;
              border-right: 2px solid #64748b;
              border-bottom: 2px solid #64748b;
              transform: rotate(-45deg);
              transition: transform 0.25s ease;
              flex-shrink: 0;
              margin-left: auto;
            }
            .skills-accordion .accordion__button[aria-expanded="true"]::after {
              transform: rotate(45deg);
            }
            .skills-accordion .accordion__button[aria-expanded="true"] {
              background: linear-gradient(135deg, #085ec8 0%, #0a6fd9 100%);
              color: white;
            }
            .skills-accordion .accordion__button[aria-expanded="true"]::after {
              border-color: white;
            }
            .skills-accordion .accordion__panel {
              padding: 1rem;
              background: #fafbfc;
              border-top: 1px solid #e2e8f0;
              display: flex;
              flex-wrap: wrap;
              gap: 0;
            }
            .skills-accordion .accordion__panel[hidden] {
              display: none;
            }
          `}</style>

          <div className="skills-accordion">
            <Accordion allowZeroExpanded allowMultipleExpanded>
              {skill_categories.map((category) => {
                const category_skills = skills.filter(
                  skill => skill.category === category
                );
                const selectedCount = category_skills.filter(
                  skill => form_elements.skills_id_in.includes(`${skill.id}`)
                ).length;

                return (
                  <AccordionItem key={category}>
                    <AccordionItemHeading>
                      <AccordionItemButton>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {category}
                          {selectedCount > 0 && (
                            <span style={{
                              background: 'linear-gradient(135deg, #10b981, #059669)',
                              color: 'white',
                              fontSize: '0.6875rem',
                              fontWeight: 700,
                              padding: '0.125rem 0.5rem',
                              borderRadius: '100px',
                              minWidth: '20px',
                              textAlign: 'center',
                            }}>
                              {selectedCount}
                            </span>
                          )}
                        </span>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel>
                      {category_skills.map(skill => (
                        <Tag
                          key={skill.id}
                          form_elements={form_elements}
                          onChangeCheckbox={onChangeCheckbox}
                          skill={skill}
                        />
                      ))}
                    </AccordionItemPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </div>
      </div>

      {/* Footer hint */}
      <div style={{
        padding: '1rem 1.5rem',
        borderTop: `1px solid ${tokens.colors.border}`,
        background: tokens.colors.bgSubtle,
        fontSize: '0.75rem',
        color: tokens.colors.textMuted,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        borderRadius: `0 0 ${tokens.radius.lg} ${tokens.radius.lg}`,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
        Results update automatically
      </div>
    </div>
  );
};

export default ProfileFilters;
